import React, { useEffect, useRef, useState } from "react";
import { Archive, Bell, BellOff, Check, EyeOff, Lock, MoreVertical, Pin, RotateCcw, Trash2 } from "lucide-react";
import type { Presence } from "./types";
import { formatLastSeen, formatRelativeTime, getIconOrder } from "./utils";

type ChatCellFolder = "inbox" | "archive" | "recycle";

type ChatCellProps = {
    avatar: string;
    title: string;
    subtitle?: string;
    time?: string;
    active?: boolean;
    onClick?: () => void;
    presence?: Presence;
    isEncrypted?: boolean;
    status?: string;
    unreadCount?: number;
    lastMessageAt?: number;
    isMuted?: boolean;
    isPinned?: boolean;
    isIncognito?: boolean;
    isGroup?: boolean;
    isSelected?: boolean;
    selectionMode?: boolean;
    chatId?: string | number;
    folder?: ChatCellFolder;
    onArchive?: () => void;
    onRecycle?: () => void;
    onRestore?: () => void;
    onPermanentDelete?: () => void;
    onLongPress?: () => void;
    onToggleEncryption?: () => void;
    onToggleMute?: () => void;
    onTogglePin?: () => void;
    onToggleIncognito?: () => void;
};

const SWIPE_LIMIT = 112;
const SWIPE_THRESHOLD = 74;
const ENABLE_E2E = false;

export default function ChatCell({
    avatar,
    title,
    subtitle,
    time,
    active,
    onClick,
    presence,
    isEncrypted,
    status: _status,
    unreadCount,
    lastMessageAt,
    isMuted,
    isPinned,
    isIncognito,
    isGroup,
    isSelected,
    selectionMode,
    chatId,
    folder = "inbox",
    onArchive,
    onRecycle,
    onRestore,
    onPermanentDelete,
    onLongPress,
    onToggleEncryption,
    onToggleMute,
    onTogglePin,
    onToggleIncognito,
}: ChatCellProps) {
    const [dragX, setDragX] = useState(0);
    const [settling, setSettling] = useState(false);
    const [rowMenuOpen, setRowMenuOpen] = useState(false);
    const shellRef = useRef<HTMLDivElement | null>(null);
    const longPressTimerRef = useRef<number | null>(null);
    const longPressStartRef = useRef<{ x: number; y: number } | null>(null);
    const pointerRef = useRef<{
        id: number;
        startX: number;
        startY: number;
        dragging: boolean;
        cancelled: boolean;
    } | null>(null);
    const blockClickRef = useRef(false);

    useEffect(() => {
        if (!rowMenuOpen) return;
        const handleOutside = (event: MouseEvent) => {
            if (shellRef.current && !shellRef.current.contains(event.target as Node)) {
                setRowMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [rowMenuOpen]);

    useEffect(() => {
        if (selectionMode) setRowMenuOpen(false);
    }, [selectionMode]);

    const iconMap: Record<string, React.ReactNode> = {};
    if (ENABLE_E2E && isEncrypted) {
        iconMap.lock = (
            <button key="lock" onClick={(event) => { event.stopPropagation(); onToggleEncryption?.(); }} className="transition hover:opacity-70" title="Disable E2E">
                <Lock size={12} className="text-teal-400" />
            </button>
        );
    }
    if (isIncognito) {
        iconMap.incognito = (
            <button key="incognito" onClick={(event) => { event.stopPropagation(); onToggleIncognito?.(); }} className="rounded-full p-0.5 transition hover:bg-cyan-400/10 hover:opacity-90" title="Incognito active">
                <EyeOff size={13} className="shrink-0 text-cyan-300" />
            </button>
        );
    }
    if (isMuted) {
        iconMap.muted = (
            <button key="muted" onClick={(event) => { event.stopPropagation(); onToggleMute?.(); }} className="transition hover:opacity-70" title="Unmute">
                <BellOff size={12} className="text-neutral-500" />
            </button>
        );
    }
    if (isPinned) {
        iconMap.pinned = (
            <button
                key="pinned"
                onClick={(event) => { event.stopPropagation(); onTogglePin?.(); }}
                className="inline-flex h-5 items-center gap-1 rounded-full border border-yellow-300/20 bg-yellow-300/10 px-1.5 text-[10px] font-semibold text-yellow-100 transition hover:bg-yellow-300/15"
                title="Unpin"
            >
                <Pin size={10} className="text-yellow-300" /> Pinned
            </button>
        );
    }

    const savedOrder = chatId ? getIconOrder(chatId) : [];
    const orderedIcons: React.ReactNode[] = [];
    const rendered = new Set<string>();
    for (const key of savedOrder) {
        if (iconMap[key]) {
            orderedIcons.push(iconMap[key]);
            rendered.add(key);
        }
    }
    for (const key of Object.keys(iconMap)) {
        if (!rendered.has(key)) orderedIcons.push(iconMap[key]);
    }

    const presenceText = isGroup
        ? (_status || "Group conversation")
        : isIncognito || presence?.incognito
        ? "Incognito"
        : presence?.hidden
            ? "Last seen unavailable"
            : presence?.online
                ? "Online"
                : formatLastSeen(presence?.lastSeen);
    const hasUnread = (unreadCount ?? 0) > 0;
    const statusColor = isGroup
        ? "bg-cyan-400"
        : isIncognito || presence?.incognito
        ? "bg-cyan-400"
        : presence?.online
            ? "bg-emerald-400"
            : "bg-neutral-600";
    const canSwipe = !selectionMode && folder === "inbox" && (onArchive || onRecycle);

    const clearLongPress = () => {
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressStartRef.current = null;
    };

    const runRowAction = (event: React.MouseEvent, action?: () => void) => {
        event.stopPropagation();
        setRowMenuOpen(false);
        action?.();
    };

    const resetSwipe = () => {
        setSettling(true);
        setDragX(0);
        window.setTimeout(() => setSettling(false), 170);
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest("button, a")) return;
        if (event.pointerType !== "mouse" && onLongPress) {
            longPressStartRef.current = { x: event.clientX, y: event.clientY };
            longPressTimerRef.current = window.setTimeout(() => {
                blockClickRef.current = true;
                pointerRef.current = null;
                setDragX(0);
                setSettling(false);
                onLongPress();
                clearLongPress();
            }, 460);
        }
        if (!canSwipe || event.pointerType === "mouse") return;
        pointerRef.current = {
            id: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            dragging: false,
            cancelled: false,
        };
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const pressStart = longPressStartRef.current;
        if (pressStart && (Math.abs(event.clientX - pressStart.x) > 10 || Math.abs(event.clientY - pressStart.y) > 10)) {
            clearLongPress();
        }
        const pointer = pointerRef.current;
        if (!pointer || pointer.id !== event.pointerId || pointer.cancelled) return;
        const dx = event.clientX - pointer.startX;
        const dy = event.clientY - pointer.startY;

        if (!pointer.dragging && Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
            pointer.cancelled = true;
            resetSwipe();
            return;
        }
        if (Math.abs(dx) < 8) return;

        pointer.dragging = true;
        blockClickRef.current = true;
        event.preventDefault();
        setDragX(Math.max(-SWIPE_LIMIT, Math.min(SWIPE_LIMIT, dx)));
    };

    const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
        clearLongPress();
        const pointer = pointerRef.current;
        if (!pointer || pointer.id !== event.pointerId) return;
        pointerRef.current = null;

        if (pointer.cancelled || !pointer.dragging) {
            resetSwipe();
            window.setTimeout(() => { blockClickRef.current = false; }, 0);
            return;
        }

        if (dragX >= SWIPE_THRESHOLD && onArchive) {
            setSettling(true);
            setDragX(160);
            window.setTimeout(() => {
                onArchive();
                setDragX(0);
                setSettling(false);
                blockClickRef.current = false;
            }, 150);
            return;
        }

        if (dragX <= -SWIPE_THRESHOLD && onRecycle) {
            setSettling(true);
            setDragX(-160);
            window.setTimeout(() => {
                onRecycle();
                setDragX(0);
                setSettling(false);
                blockClickRef.current = false;
            }, 150);
            return;
        }

        resetSwipe();
        window.setTimeout(() => { blockClickRef.current = false; }, 0);
    };

    const handleClick = () => {
        if (blockClickRef.current) {
            blockClickRef.current = false;
            return;
        }
        onClick?.();
    };

    const handlePointerCancel = () => {
        clearLongPress();
        pointerRef.current = null;
        resetSwipe();
        window.setTimeout(() => { blockClickRef.current = false; }, 0);
    };

    return (
        <div
            ref={shellRef}
            className={`bs-chat-swipe-shell bs-chat-cell-shell relative mx-2 my-1 overflow-visible rounded-lg ${
                rowMenuOpen ? "z-[500]" : "z-0"
            }`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerCancel}
        >
            {folder === "inbox" && (
                <div className="bs-chat-swipe-actions pointer-events-none absolute inset-0 flex items-center justify-between rounded-lg">
                    <div className={`bs-chat-swipe-action bs-chat-swipe-archive flex h-full min-w-[8rem] items-center gap-2 bg-emerald-500/15 px-4 text-emerald-100 transition-opacity ${dragX > 12 ? "opacity-100" : "opacity-0"}`}>
                        <Archive size={17} />
                        <span className="text-xs font-black">Archive</span>
                    </div>
                    <div className={`bs-chat-swipe-action bs-chat-swipe-recycle ml-auto flex h-full min-w-[9rem] items-center justify-end gap-2 bg-rose-500/15 px-4 text-rose-100 transition-opacity ${dragX < -12 ? "opacity-100" : "opacity-0"}`}>
                        <Trash2 size={17} />
                        <span className="text-xs font-black">Recycle</span>
                    </div>
                </div>
            )}
            <div
                onClick={handleClick}
                style={{ transform: `translateX(${dragX}px)` }}
                className={`bs-chat-cell-card group/chat relative flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-3 transition-all duration-200 ${
                    settling ? "ease-out" : ""
                } ${
                    isSelected
                        ? "border-cyan-300/60 bg-cyan-400/10 shadow-[0_0_0_1px_rgba(34,211,238,0.18)]"
                        :
                    active
                        ? "border-cyan-500/60 bg-cyan-500/10 shadow-[0_0_0_1px_rgba(34,211,238,0.12)]"
                        : "border-transparent bg-neutral-950 hover:border-neutral-800 hover:bg-neutral-900/80"
                } ${isIncognito ? "bg-cyan-950/10" : ""}`}
            >
                <div className="relative">
                    <img src={avatar} alt={title} className="bs-chat-cell-avatar h-11 w-11 rounded-full object-cover ring-1 ring-white/10" />
                    {!isGroup && <span className="absolute -bottom-0.5 -right-0.5">
                        <span className={`bs-chat-cell-status-dot inline-block h-3 w-3 rounded-full border-2 border-black ${statusColor}`} />
                    </span>}
                    {isSelected && (
                        <span className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-cyan-100/50 bg-cyan-300 text-black shadow-lg shadow-cyan-500/20">
                            <Check size={12} strokeWidth={3} />
                        </span>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className={`truncate text-sm font-semibold ${hasUnread ? "text-white" : "text-neutral-100"}`}>{title}</h3>
                        <div className="flex shrink-0 items-center gap-1">{orderedIcons}</div>
                    </div>
                    {subtitle && <p className={`mt-0.5 truncate text-xs ${hasUnread ? "font-medium text-neutral-200" : "text-neutral-500"}`}>{subtitle}</p>}
                    <p className={`mt-1 truncate text-[11px] ${isIncognito || presence?.incognito ? "text-cyan-300/80" : "text-neutral-600"}`}>{presenceText}</p>
                </div>
                {selectionMode ? (
                    <div className="w-8 shrink-0" />
                ) : folder === "archive" ? (
                    <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onRestore?.(); }}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-400/25 bg-emerald-400/10 px-2.5 py-1.5 text-xs font-black text-emerald-100 transition hover:bg-emerald-400/15"
                    >
                        <RotateCcw size={13} /> Restore
                    </button>
                ) : folder === "recycle" ? (
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); onRestore?.(); }}
                            className="inline-flex items-center gap-1 rounded-md border border-emerald-400/25 bg-emerald-400/10 px-2 py-1 text-[11px] font-black text-emerald-100 transition hover:bg-emerald-400/15"
                        >
                            <RotateCcw size={12} /> Restore
                        </button>
                        <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); onPermanentDelete?.(); }}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-400/25 bg-rose-500/10 px-2 py-1 text-[11px] font-black text-rose-100 transition hover:bg-rose-500/15"
                        >
                            <Trash2 size={12} /> Delete
                        </button>
                    </div>
                ) : (
                    <div className="flex shrink-0 flex-col items-end gap-1">
                        {lastMessageAt ? (
                            <span className={`text-[11px] ${hasUnread ? "text-cyan-300" : "text-neutral-500"}`}>{formatRelativeTime(lastMessageAt)}</span>
                        ) : time ? (
                            <span className="text-[11px] text-neutral-500">{time}</span>
                        ) : null}
                        {(unreadCount ?? 0) > 0 && (
                            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-cyan-400 px-1.5 text-[11px] font-bold leading-none text-black">
                                {unreadCount! > 99 ? "99+" : unreadCount}
                            </span>
                        )}
                    </div>
                )}
                {!selectionMode && (
                <div className="relative shrink-0">
                    <button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            setRowMenuOpen(open => !open);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-neutral-800 bg-black/30 text-neutral-400 opacity-100 transition hover:border-cyan-300/35 hover:bg-cyan-400/10 hover:text-cyan-100 focus:opacity-100 sm:opacity-0 sm:group-hover/chat:opacity-100"
                        data-chat-row-menu-trigger="true"
                        title="Chat options"
                        aria-label="Chat options"
                    >
                        <MoreVertical size={15} />
                    </button>
                    {rowMenuOpen && (
                        <div className="bs-chat-cell-menu absolute right-0 top-9 z-[100] w-44 overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950/98 p-1 shadow-2xl shadow-black/50">
                            {folder === "inbox" && (
                                <>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onArchive)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-neutral-200 transition hover:bg-neutral-900"
                                    >
                                        <Archive size={13} className="text-emerald-300" /> Archive
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onTogglePin)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-neutral-200 transition hover:bg-neutral-900"
                                    >
                                        <Pin size={13} className="text-yellow-300" /> {isPinned ? "Unpin" : "Pin"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onRecycle)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-rose-100 transition hover:bg-rose-500/10"
                                    >
                                        <Trash2 size={13} /> Recycle Bin
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onToggleMute)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-neutral-200 transition hover:bg-neutral-900"
                                    >
                                        {isMuted ? <Bell size={13} className="text-amber-200" /> : <BellOff size={13} className="text-amber-300" />}
                                        {isMuted ? "Unmute" : "Mute"}
                                    </button>
                                </>
                            )}
                            {folder === "archive" && (
                                <>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onRestore)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/10"
                                    >
                                        <RotateCcw size={13} /> Restore
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onRecycle)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-rose-100 transition hover:bg-rose-500/10"
                                    >
                                        <Trash2 size={13} /> Recycle Bin
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onToggleMute)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-neutral-200 transition hover:bg-neutral-900"
                                    >
                                        {isMuted ? <Bell size={13} className="text-amber-200" /> : <BellOff size={13} className="text-amber-300" />}
                                        {isMuted ? "Unmute" : "Mute"}
                                    </button>
                                </>
                            )}
                            {folder === "recycle" && (
                                <>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onRestore)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/10"
                                    >
                                        <RotateCcw size={13} /> Restore
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(event) => runRowAction(event, onPermanentDelete)}
                                        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-semibold text-rose-100 transition hover:bg-rose-500/10"
                                    >
                                        <Trash2 size={13} /> Delete permanently
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}
